import type { PivotedRow } from '@data/components/report/ReportProficiencyGraph/ReportProficiencyGraph';
import type { MetricsDatasetV2Response } from '@data/hooks/api/use-metrics-dataset-v2-api/use-metrics-dataset-v2-api';

export interface PivotDatasetForProficiencyGraphResult {
  rows: PivotedRow[];
  proficiencyOrder: string[];
  presentAssessmentPeriods: Set<string>;
}

/**
 * Pivots a metrics dataset response into rows suitable for the
 * `ReportProficiencyGraph`. Each row corresponds to an assessment period
 * (in `fields.benchmark.order`), with one column per proficiency level
 * (in `fields.proficiency.order`) holding the student count for that
 * (assessment period × proficiency) pair (defaulting to `0`).
 *
 * Also returns:
 * - `proficiencyOrder`: the proficiency levels in display order
 * - `presentAssessmentPeriods`: the set of assessment periods that actually
 *   appear in the data rows (vs. just the field's declared `order`)
 */
export function pivotDatasetForProficiencyGraph(
  dataset: MetricsDatasetV2Response | undefined
): PivotDatasetForProficiencyGraphResult {
  if (!dataset)
    return {
      rows: [],
      proficiencyOrder: [],
      presentAssessmentPeriods: new Set(),
    };

  const assessmentPeriodIdx = dataset.fields.findIndex(
    (f) => f.key === 'benchmark' || f.key === 'assessment_period'
  );
  const proficiencyIdx = dataset.fields.findIndex(
    (f) => f.key === 'proficiency'
  );
  const studentCountIdx = dataset.fields.findIndex(
    (f) => f.key === 'student_count'
  );

  const assessmentPeriodOrder =
    dataset.fields[assessmentPeriodIdx]?.order ?? [];
  const proficiencyOrder = dataset.fields[proficiencyIdx]?.order ?? [];

  const rowsByAssessmentPeriod = new Map<string, PivotedRow>(
    assessmentPeriodOrder.map((assessmentPeriod) => {
      const seeded: PivotedRow = { assessmentPeriod };
      proficiencyOrder.forEach((p) => {
        seeded[p] = 0;
      });
      return [assessmentPeriod, seeded];
    })
  );

  const presentAssessmentPeriods = new Set<string>();
  dataset.data.forEach((row) => {
    const assessmentPeriod = row[assessmentPeriodIdx] as string;
    const proficiency = row[proficiencyIdx] as string;
    const count = row[studentCountIdx] as number | null;
    const target = rowsByAssessmentPeriod.get(assessmentPeriod);
    if (target) target[proficiency] = count ?? 0;
    if (assessmentPeriod) presentAssessmentPeriods.add(assessmentPeriod);
  });

  return {
    rows: assessmentPeriodOrder.map((b) => rowsByAssessmentPeriod.get(b)!),
    proficiencyOrder,
    presentAssessmentPeriods,
  };
}
